import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import LandingNav from '../landing/components/LandingNav';
import LandingFooter from '../landing/components/LandingFooter';
import TeamMemberCard from './components/TeamMemberCard';
import AdvisorCard from './components/AdvisorCard';

const teamMembers = [
  {
    name: 'Alexandra Hartley',
    title: 'Chief Executive Officer',
    bio: 'Former Managing Director at a leading infrastructure fund with 18 years of experience structuring cross-border equity transactions across Europe and Asia-Pacific. Alexandra founded SeedDrive to address the operational fragmentation she experienced first-hand in institutional deal management.',
    initials: 'AH',
  },
  {
    name: 'Marcus Osei',
    title: 'Chief Technology Officer',
    bio: 'Previously led platform engineering at a regulated fintech serving Tier 1 asset managers. Marcus brings deep expertise in secure financial infrastructure, API design, and regulatory technology, having built compliance-grade systems across multiple jurisdictions.',
    initials: 'MO',
  },
  {
    name: 'Priya Nair',
    title: 'Head of Compliance & Legal',
    bio: 'Qualified solicitor with a decade of experience in alternative investment regulation, including AIFMD implementation, FCA authorisation, and cross-border fund structuring. Priya leads SeedDrive\'s regulatory strategy and institutional compliance frameworks.',
    initials: 'PN',
  },
  {
    name: 'James Whitfield',
    title: 'Head of Product',
    bio: 'Product leader with a background in institutional investment platforms and enterprise SaaS. James has shipped products used by over 200 institutional investors and brings a rigorous, user-research-driven approach to building tools for complex financial workflows.',
    initials: 'JW',
  },
];

const advisors = [
  {
    name: 'Dame Caroline Forsythe',
    title: 'Strategic Adviser — Regulatory Affairs',
    bio: 'Former senior regulator with 25 years at the FCA and European Securities and Markets Authority. Caroline provides strategic guidance on regulatory positioning, policy engagement, and institutional market access across UK and EU jurisdictions.',
    initials: 'CF',
  },
  {
    name: 'Dr. Ravi Krishnamurthy',
    title: 'Strategic Adviser — Infrastructure Markets',
    bio: 'Founding partner of a pan-African infrastructure equity fund and former World Bank infrastructure specialist. Ravi advises on emerging market deal flow, development finance institution relationships, and sustainable infrastructure investment frameworks.',
    initials: 'RK',
  },
];

export default function AboutPage() {
  const [selectedMember, setSelectedMember] = useState(null);

  return (
    <div className="min-h-screen" style={{ background: '#F2F0EC' }}>
      <LandingNav />
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 lg:px-8" style={{ background: '#F2F0EC' }}>
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-caption font-medium mb-6 border" style={{ borderColor: 'rgba(59,76,76,0.15)', color: 'var(--color-secondary)', background: 'rgba(59,76,76,0.04)' }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#3b4c4c' }}></span>
              About SeedDrive
            </div>
            <h1 className="font-heading text-4xl lg:text-5xl font-semibold leading-tight mb-5" style={{ color: 'var(--color-primary)' }}>Built by practitioners, for institutional markets.</h1>
            <p className="text-lg text-gray-600 font-body leading-relaxed">SeedDrive was founded to solve the infrastructure equity workflow problem from the inside — by people who have lived it.</p>
          </div>
        </div>
      </section>
      {/* Mission */}
      <section className="">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="font-heading text-3xl font-semibold mb-5" style={{ color: 'var(--color-primary)' }}>Our mission</h2>
              <p className="text-gray-700 font-body leading-relaxed text-lg mb-5">
                SeedDrive exists to bring infrastructure equity into the modern era of regulated digital finance. Our mission is to automate the operational complexity of institutional deal management — from origination to exit — while maintaining the compliance rigour that regulated markets demand.
              </p>
              <p className="text-gray-600 font-body leading-relaxed">
                We believe that infrastructure equity — the asset class that funds roads, energy grids, water systems, and digital infrastructure — deserves the same quality of tooling as public markets. Today, it runs on spreadsheets, email chains, and disconnected legal documents. SeedDrive is changing that.
              </p>
            </div>
            <div>
              <h2 className="font-heading text-3xl font-semibold mb-5" style={{ color: 'var(--color-primary)' }}>The founding story</h2>
              <p className="text-gray-700 font-body leading-relaxed mb-5">
                SeedDrive was founded in 2022 after our CEO spent years managing infrastructure equity transactions at a major fund and repeatedly encountered the same problem: the operational infrastructure for managing deals was decades behind the sophistication of the investment strategies themselves.
              </p>
              <p className="text-gray-600 font-body leading-relaxed">
                SPV formation took weeks of legal back-and-forth. Investor onboarding was manual and error-prone. Portfolio reporting required bespoke spreadsheet models rebuilt for every fund. The team set out to build the platform they wished had existed — and to make it available to the entire institutional market.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Team */}
      <section className="py-20 px-6 lg:px-8" style={{ background: '#ECEAE4' }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="font-heading text-3xl font-semibold mb-3" style={{ color: 'var(--color-primary)' }}>The team</h2>
            <p className="text-gray-600 font-body">Experienced practitioners from institutional finance, regulatory technology, and enterprise software.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {teamMembers?.map((member) => (
              <TeamMemberCard
                key={member?.name}
                member={member}
                onClick={() => setSelectedMember(member)}
              />
            ))}
          </div>
        </div>
      </section>
      {/* Advisors */}
      <section className="">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="font-heading text-3xl font-semibold mb-3" style={{ color: 'var(--color-primary)' }}>Advisers</h2>
            <p className="text-gray-600 font-body">Strategic guidance from leaders in regulation, infrastructure finance, and institutional markets.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
            {advisors?.map((advisor) => (
              <AdvisorCard key={advisor?.name} advisor={advisor} />
            ))}
          </div>
        </div>
      </section>
      {/* Hiring banner */}
      <section className="py-16 px-6 lg:px-8" style={{ background: 'var(--color-primary)' }}>
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
              <span className="text-xs font-caption font-medium text-green-300 uppercase tracking-wider">We're hiring</span>
            </div>
            <h2 className="font-heading text-2xl font-semibold text-white mb-2">Join us in building the future of infrastructure equity</h2>
            <p className=" font-body text-sm">We're growing our team across engineering, compliance, product, and business development. Remote-friendly, London-headquartered.</p>
          </div>
          <Link
            to="/contact"
            className=""
            style={{ color: 'var(--color-primary)' }}
          >
            View open roles
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>
      </section>
      {/* Team member modal */}
      {selectedMember && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={() => setSelectedMember(null)}
        >
          <div
            className=""
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="flex items-start gap-4 mb-5">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-heading font-semibold text-white flex-shrink-0"
                style={{ background: 'var(--color-primary)' }}
              >
                {selectedMember?.initials}
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold" style={{ color: 'var(--color-primary)' }}>{selectedMember?.name}</h3>
                <p className="text-sm font-caption text-gray-500">{selectedMember?.title}</p>
              </div>
            </div>
            <p className="text-gray-600 font-body leading-relaxed text-sm">{selectedMember?.bio}</p>
            <button
              onClick={() => setSelectedMember(null)}
              className="mt-6 w-full py-2.5 rounded-md text-sm font-caption font-medium border transition-colors hover:bg-gray-50"
              style={{ borderColor: 'var(--color-border)', color: 'var(--color-primary)' }}
            >
              Close
            </button>
          </div>
        </div>
      )}
      <LandingFooter />
    </div>
  );
}
